import { Trans, useLingui } from '@lingui/react/macro';

import { ClientAmountDueBox } from '@/components/ClientAmountDueBox';
import { RequestOrganizationPrice } from '@/components/RequestOrganizationPrice';
import { RequestWorkshopPrice } from '@/components/RequestWorkshopPrice';
import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconArchive from '@/icons/Archive.svg';
import IconClock from '@/icons/Clock.svg';
import IconTask from '@/icons/Task.svg';
import {
  ArchivedArticlesCardList,
  ArchivedArticlesTable,
  CompletedArticlesCardList,
  CompletedArticlesTable,
  OnGoingArticlesCardList,
} from '@/routes/Requests/components/ArticlesTable';
import { OnGoingArticlesTable } from '@/routes/Requests/components/ArticlesTable/OnGoingArticlesTable';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import './RequestArticles.css';

const { block, element } = createBEMClasses('request-articles');

export const RequestArticles = () => {
  const { t } = useLingui();
  const { isMobile, isWidescreen } = useViewPort();

  const {
    request,
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  const articlesWithTaskToDo = request.articles.filter((article) => !!article.taskType);

  const completedArticles = request.articles.filter(
    (article) =>
      !!article.archivedAt &&
      (article.archivalDetail?.type === 'completed' ||
        article.archivalDetail?.type === 'in-recycling')
  );

  const waitingForOthersArticles = request.articles.filter(
    (article) => !articlesWithTaskToDo.includes(article) && !completedArticles.includes(article)
  );

  return (
    <Stack gap="1rem" className={block()}>
      {articlesWithTaskToDo.length > 0 && (
        <Box padding="0">
          <Stack>
            <Stack className={element('pending-tasks')} row>
              <IconTask />
              <InlineText weight="medium">
                <Trans id="request.articles.table.pending-tasks.label">Pending item tasks</Trans>
              </InlineText>
            </Stack>
            {isMobile ? (
              <OnGoingArticlesCardList
                articles={articlesWithTaskToDo}
                mode="task"
                label={t({
                  id: 'request.articles.table.pending-tasks.label',
                  message: 'Pending item tasks',
                })}
              />
            ) : (
              <OnGoingArticlesTable articles={articlesWithTaskToDo} mode="task" />
            )}
          </Stack>
        </Box>
      )}
      {waitingForOthersArticles.length > 0 && (
        <Box padding="0">
          <Stack>
            <Stack className={element('awaiting-tasks')} row>
              <IconClock />
              <InlineText weight="medium">
                <Trans id="request.articles.table.waiting-tasks.label">Waiting for others</Trans>
              </InlineText>
            </Stack>
            {isMobile ? (
              <OnGoingArticlesCardList
                articles={waitingForOthersArticles}
                mode="step"
                label={t({
                  id: 'request.articles.table.waiting-tasks.label',
                  message: 'Waiting for others',
                })}
              />
            ) : (
              <OnGoingArticlesTable articles={waitingForOthersArticles} mode="step" />
            )}
          </Stack>
        </Box>
      )}
      {completedArticles.length > 0 && (
        <Box padding="0">
          <Stack>
            <Stack className={element('completed-articles')} row>
              <IconClock />
              <InlineText weight="medium">
                <Trans id="request.articles.table.completed.label">Completed items</Trans>
              </InlineText>
            </Stack>
            {isMobile ? (
              <CompletedArticlesCardList articles={completedArticles} />
            ) : (
              <CompletedArticlesTable articles={completedArticles} />
            )}
          </Stack>
        </Box>
      )}
      {request.archivedArticles.length > 0 && (
        <Box padding="0">
          <Stack>
            <Stack className={element('archived-articles')} row>
              <IconArchive />
              <InlineText weight="medium">
                <Trans id="request.articles.table.archived-articles.label">Archived items</Trans>
              </InlineText>
            </Stack>
            {isMobile ? (
              <ArchivedArticlesCardList articles={request.archivedArticles} />
            ) : (
              <ArchivedArticlesTable articles={request.archivedArticles} />
            )}
          </Stack>
        </Box>
      )}
      <Stack row={isWidescreen} gap="1rem" alignItems={isWidescreen ? 'flex-start' : 'stretch'}>
        {workshopPrice.shown && <RequestWorkshopPrice />}
        {organizationPrice.shown && <RequestOrganizationPrice />}
      </Stack>

      <ClientAmountDueBox />
    </Stack>
  );
};
